import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MdCalendarToday, MdAccessTime, MdArrowBack } from "react-icons/md";
import { FaEnvelope, FaRegClock } from "react-icons/fa";
import { postTimeCapsule } from "../../../services/timeCapsuleService";
import { getCurrentPackageUser } from "../../../services/api";
import Notiflix from "notiflix";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

export default function NewCapsule() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [profileData, setProfileData] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);

  // Form state'leri
  const [formData, setFormData] = useState({
    fullname: "",
    title: "",
    description: "",
    content: "",
    date: "",
    time: "",
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  // Notiflix init
  useEffect(() => {
    Notiflix.Notify.init({
      position: "top-right",
      timeout: 3000,
      clickToClose: true,
      pauseOnHover: true,
    });
  }, []);

  // Kullanıcı bilgilerini çek
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getCurrentPackageUser(id);
        setProfileData(userData);
      } catch (error) {
        console.error("Kullanıcı bilgileri alınamadı:", error);
        Notiflix.Notify.failure("Kullanıcı bilgileri alınamadı!");
      }
    };

    if (id) {
      fetchUserData();
    }
  }, [id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    if (date) {
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      setFormData((prev) => ({
        ...prev,
        date: `${year}-${month}-${day}`,
      }));
    }
    setShowDatePicker(false);
  };

  const handleTimeChange = (time) => {
    setSelectedTime(time);
    if (time) {
      const hours = String(time.getHours()).padStart(2, "0");
      const minutes = String(time.getMinutes()).padStart(2, "0");
      setFormData((prev) => ({
        ...prev,
        time: `${hours}:${minutes}`,
      }));
    }
    setShowTimePicker(false);
  };

  const validateStep = () => {
    if (currentStep === 1) {
      if (!formData.fullname.trim()) {
        Notiflix.Notify.failure("Lütfen adınızı soyadınızı giriniz!");
        return false;
      }
    } else if (currentStep === 2) {
      if (!formData.title.trim()) {
        Notiflix.Notify.failure("Lütfen bir başlık giriniz!");
        return false;
      }
    } else if (currentStep === 3) {
      if (!formData.description.trim()) {
        Notiflix.Notify.failure("Lütfen bir açıklama giriniz!");
        return false;
      }
    } else if (currentStep === 4) {
      if (!formData.content.trim()) {
        Notiflix.Notify.failure("Lütfen içerik giriniz!");
        return false;
      }
    } else if (currentStep === 5) {
      if (!formData.date || !formData.time) {
        Notiflix.Notify.failure("Lütfen tarih ve saat seçiniz!");
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (!validateStep()) return;

    if (currentStep === 5) {
      handleSendCapsule();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatDateTime = (date, time) => {
    return `${date} ${time}:00`;
  };

  const handleSendCapsule = async () => {
    setIsSending(true);

    try {
      const unlockDate = formatDateTime(formData.date, formData.time);

      const timeCapsuleData = {
        fullname: formData.fullname.trim(),
        title: formData.title.trim(),
        description: formData.description.trim(),
        content: formData.content.trim(),
        unlock_date: unlockDate,
        timezone: "Europe/Istanbul",
        receiver_id: id,
        receiver_mail: profileData?.email || "",
      };

      const response = await postTimeCapsule(timeCapsuleData);

      if (response.success) {
        setShowSuccess(true);
      } else {
        throw new Error(response.message || "Zaman kapsülü gönderilemedi");
      }
    } catch (error) {
      console.error("Zaman kapsülü gönderilirken hata:", error);
      Notiflix.Notify.failure(
        error.response?.data?.message ||
          error.message ||
          "Zaman kapsülü gönderilirken bir hata oluştu!"
      );
    } finally {
      setIsSending(false);
    }
  };

  // Animation variants
  const pageVariants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  const buttonVariants = {
    hover: { scale: 1.02 },
    tap: { scale: 0.98 },
  };

  const progressPercentage = (currentStep / 5) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#3DA7B5] via-[#4FB3BF] to-[#5ABCC8] flex flex-col items-center justify-center p-4 relative">
      {/* Ana Sayfa Butonu - Sol Üst */}
      <button
        onClick={() => navigate("/login")}
        className="absolute top-4 left-4 z-20 p-2.5 bg-white/90 hover:bg-white rounded-full shadow-lg transition-all duration-300 hover:scale-110"
        aria-label="Ana Sayfa"
      >
        <svg className="w-6 h-6 text-[#3DA7B5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
      </button>

      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
      >
        {!showSuccess ? (
          <>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] p-6 relative">
          <button
            onClick={() => navigate(`/${id}`)}
            className="absolute left-4 top-6 text-white hover:bg-white/20 rounded-full p-2 transition"
          >
            <MdArrowBack className="text-xl" />
          </button>
          <div className="text-center">
            <FaEnvelope className="text-white text-4xl mx-auto mb-2" />
            <h1 className="text-white text-2xl font-bold">Zaman Kapsülü</h1>
            <p className="text-white/90 text-sm mt-1">
              {profileData?.first_name} {profileData?.last_name}'e özel bir mesaj bırak
            </p>
          </div>

          {/* Progress Bar */}
          <div className="mt-4 bg-white/30 rounded-full h-2 overflow-hidden">
            <motion.div
              className="h-full bg-white rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progressPercentage}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <div className="text-center mt-2">
            <span className="text-white text-xs font-medium">
              Adım {currentStep} / 5
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[500px]">
          <AnimatePresence mode="wait">
            {/* Step 1: İsim Soyisim */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Kimden Geldiğini Belirt
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Adını ve soyadını gir
                </p>
                <input
                  type="text"
                  name="fullname"
                  placeholder="Örn: Ahmet Yılmaz"
                  value={formData.fullname}
                  onChange={handleInputChange}
                  maxLength={50}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#3DA7B5] focus:outline-none text-gray-700 placeholder-gray-400 transition bg-white"
                />
                <p className="text-xs text-gray-400 mt-2 text-right">
                  {formData.fullname.length} / 50
                </p>
              </motion.div>
            )}

            {/* Step 2: Başlık */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Başlık Belirle
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Zaman kapsülün için kısa ve öz bir başlık seç
                </p>
                <input
                  type="text"
                  name="title"
                  placeholder="Örn: Gelecekteki Bana..."
                  value={formData.title}
                  onChange={handleInputChange}
                  maxLength={100}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#3DA7B5] focus:outline-none text-gray-700 placeholder-gray-400 transition bg-white"
                />
                <p className="text-xs text-gray-400 mt-2 text-right">
                  {formData.title.length} / 100
                </p>
              </motion.div>
            )}

            {/* Step 3: Açıklama */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Açıklama Ekle
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Kapsülün hakkında kısa bir açıklama yaz
                </p>
                <textarea
                  name="description"
                  placeholder="Bu kapsülde ne var? Kısaca anlat..."
                  value={formData.description}
                  onChange={handleInputChange}
                  maxLength={200}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#3DA7B5] focus:outline-none text-gray-700 placeholder-gray-400 resize-none transition bg-white"
                />
                <p className="text-xs text-gray-400 mt-2 text-right">
                  {formData.description.length} / 200
                </p>
              </motion.div>
            )}

            {/* Step 4: İçerik */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Mesajını Yaz
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Gelecekte açılacak özel mesajını buraya yaz
                </p>
                <textarea
                  name="content"
                  placeholder="Sevgili gelecekteki ben/arkadaşım,&#10;&#10;Bugün..."
                  value={formData.content}
                  onChange={handleInputChange}
                  maxLength={1000}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-[#3DA7B5] focus:outline-none text-gray-700 placeholder-gray-400 resize-none transition bg-white"
                />
                <p className="text-xs text-gray-400 mt-2 text-right">
                  {formData.content.length} / 1000
                </p>
              </motion.div>
            )}

            {/* Step 5: Tarih ve Saat */}
            {currentStep === 5 && (
              <motion.div
                key="step5"
                variants={pageVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-xl font-bold text-gray-800 mb-2">
                  Ne Zaman Açılsın?
                </h2>
                <p className="text-gray-600 text-sm mb-6">
                  Kapsülün hangi tarih ve saatte açılacağını belirle
                </p>

                {/* Tarih Seçimi */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tarih
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={
                        formData.date
                          ? new Date(formData.date).toLocaleDateString("tr-TR")
                          : ""
                      }
                      placeholder="Tarih seçiniz"
                      readOnly
                      onClick={() => setShowDatePicker(true)}
                      className="w-full px-4 py-3 pl-12 rounded-xl border-2 border-gray-200 focus:border-[#3DA7B5] focus:outline-none text-gray-700 placeholder-gray-400 cursor-pointer transition bg-white"
                    />
                    <MdCalendarToday className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                    {showDatePicker && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowDatePicker(false)}>
                        <div className="bg-white border-2 border-gray-200 rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
                          <DatePicker
                            selected={selectedDate}
                            onChange={handleDateChange}
                            inline
                            minDate={new Date()}
                            dateFormat="dd/MM/yyyy"
                            locale="tr"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Saat Seçimi */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Saat
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={formData.time || ""}
                      placeholder="Saat seçiniz"
                      readOnly
                      onClick={() => setShowTimePicker(true)}
                      className="w-full px-4 py-3 pl-12 rounded-xl border-2 border-gray-200 focus:border-[#3DA7B5] focus:outline-none text-gray-700 placeholder-gray-400 cursor-pointer transition bg-white"
                    />
                    <MdAccessTime className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
                    {showTimePicker && (
                      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setShowTimePicker(false)}>
                        <div className="bg-white border-2 border-gray-200 rounded-xl shadow-xl" onClick={(e) => e.stopPropagation()}>
                          <DatePicker
                            selected={selectedTime}
                            onChange={handleTimeChange}
                            showTimeSelect
                            showTimeSelectOnly
                            timeIntervals={15}
                            timeCaption="Saat"
                            dateFormat="HH:mm"
                            timeFormat="HH:mm"
                            inline
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Özet Bilgi */}
                {formData.date && formData.time && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 bg-gradient-to-r from-[#3DA7B5]/10 to-[#5ABCC8]/10 rounded-xl border border-[#3DA7B5]/20"
                  >
                    <div className="flex items-center gap-2 text-[#3DA7B5]">
                      <FaRegClock className="text-lg" />
                      <div>
                        <p className="text-sm font-medium">Açılış Zamanı</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {new Date(formData.date).toLocaleDateString("tr-TR", {
                            day: "numeric",
                            month: "long",
                            year: "numeric",
                          })}{" "}
                          - {formData.time}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Buttons */}
          <div className="flex gap-3 mt-8">
            {currentStep > 1 && (
              <motion.button
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                onClick={handlePrev}
                disabled={isSending}
                className="flex-1 py-3 px-6 rounded-xl border-2 border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Geri
              </motion.button>
            )}
            <motion.button
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              onClick={handleNext}
              disabled={isSending}
              className={`${
                currentStep === 1 ? "w-full" : "flex-1"
              } py-3 px-6 rounded-xl bg-gradient-to-r from-[#3DA7B5] to-[#5ABCC8] text-white font-medium shadow-lg hover:shadow-xl transition disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isSending
                ? "Gönderiliyor..."
                : currentStep === 4
                ? "Gönder"
                : "İleri"}
            </motion.button>
          </div>
        </div>
          </>
        ) : (
          /* Success Screen */
          <div className="p-8 sm:p-12 text-center space-y-6">

            {/* Success Message */}
            <div className="space-y-3">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#3DA7B5] font-poppins">Tebrikler! 🎉</h2>
              <p className="text-gray-700 text-base sm:text-lg font-medium font-poppins">
                Zaman kapsülünüz başarıyla <span className="font-bold text-[#3DA7B5]">{profileData?.first_name} {profileData?.last_name}</span>'e gönderildi!
              </p>
            </div>

            {/* Divider */}
            <div className="border-t-2 border-gray-200 my-6"></div>

            {/* Jodiac Promotion */}
            <div className="space-y-4 bg-[#E8F5F7] border-2 border-[#3DA7B5]/30 p-6 rounded-2xl">
              <h3 className="text-xl font-bold text-[#3DA7B5] font-poppins">Sen de zaman kapsülü almak ister misin?</h3>
              <p className="text-gray-700 text-sm leading-relaxed font-poppins">
                <span className="font-bold text-[#3DA7B5]">Jodiac</span>, şık ve akıllı bilekliğin ile farklısın! ✨
              </p>
              <a
                href="https://www.jodi.ac"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full py-3 px-6 bg-[#3DA7B5] hover:bg-[#2a8a96] text-white font-bold rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg font-poppins"
              >
                Hemen Satın Al →
              </a>
            </div>
          </div>
        )}
      </motion.div>

      {/* Jodiac Branding - Kartın Altında */}
      {showSuccess && (
        <div className="flex flex-col items-center justify-center mt-6 space-y-2 w-full max-w-md">
          <img 
            src="/assets/pngImages/jodiacLogoML.png" 
            alt="Jodiac Logo" 
            className="h-10 sm:h-12 w-auto opacity-80 hover:opacity-100 transition-opacity"
          />
          <a 
            href="https://www.jodi.ac" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-white text-xs sm:text-sm font-semibold hover:text-yellow-300 transition-colors"
          >
            www.jodi.ac
          </a>
        </div>
      )}
    </div>
  );
}

